import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ChartModule, UIChart } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { IDashboardData } from '../../../Utils/_models/dashboard.model';
import { LoaderService } from '../../../Utils/_services/loader.service';
import { AdminService } from '../../../Utils/_services/admin/admin/admin.service';
import { CountUpDirective } from '../../../Utils/_directives/count-up.directive';
import { IKpis } from './dashbboard.model';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';
import { SkeletonModule } from 'primeng/skeleton';
import { signal } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, CardModule, ChartModule, TableModule, TooltipModule, TagModule, AvatarModule, ButtonModule, MenuModule, CountUpDirective, SpinnerLoaderComponent, SkeletonModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements AfterViewInit {
  @ViewChild('revChart') chart?: UIChart;

  userName = 'Anima';
  userAvatar = 'Peets Warrior';

  isLoading = signal(true);

  kpis: Array<IKpis> = [];
  chartData: any;
  chartOptions: any;
  chartPlugins = [
    {
      id: 'verticalLine',
      afterDraw: (chart: any) => {
        if (chart.tooltip?._active?.length) {
          const activePoint = chart.tooltip._active[0];
          const ctx = chart.ctx;
          const x = activePoint.element.x;
          const topY = chart.scales.y.top;
          const bottomY = chart.scales.y.bottom;

          ctx.save();
          ctx.beginPath();
          ctx.setLineDash([4, 4]);
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = '#818cf8';
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  ];

  recentLobbies: any[] = [];
  recentUsers: any[] = [];
  chartMenuItems: MenuItem[] | undefined;
  chartTitle: string = 'Revenue by Day';

  constructor(
    private adminService: AdminService,
    private loaderService: LoaderService,
    private router: Router
  ) { }

  ngOnInit() {
    this.chartMenuItems = [
      {
        label: 'Year [Jan 1-Dec 31]',
        command: () => { this.chartTitle = 'Revenue by Year'; this.loadDashboard('1y'); }
      },
      {
        label: 'Month [Current Month - End of current month]',
        command: () => { this.chartTitle = 'Revenue by Month'; this.loadDashboard('1m'); }
      },
      {
        label: 'Day [Current week to end of week]',
        command: () => { this.chartTitle = 'Revenue by Day'; this.loadDashboard('1w'); }
      }
    ];

    const textColor = '#6b7280';
    const gridColor = '#f3f4f6';
    this.chartData = {
      labels: [],
      datasets: [
        {
          label: 'Revenue',
          data: [],
          fill: true,
          borderColor: '#ec4899',
          backgroundColor: 'rgba(236, 72, 153, 0.04)',
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#ec4899',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          pointBackgroundColor: '#ec4899',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }
      ]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: '#070919',
          titleColor: '#94a3b8',
          titleFont: {
            size: 11,
            weight: 'normal'
          },
          bodyColor: '#ffffff',
          bodyFont: {
            size: 14,
            weight: 'bold'
          },
          padding: {
            top: 8,
            bottom: 8,
            left: 12,
            right: 12
          },
          cornerRadius: 6,
          displayColors: false,
          callbacks: {
            title: (context: any) => 'Sales',
            label: (context: any) => {
              const val = context.parsed.y;
               return val !== undefined ? `$${val.toLocaleString()}` : '';
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: textColor,
            font: {
              size: 12
            }
          },
          border: {
            color: '#e5e7eb'
          }
        },
        y: {
          grid: {
            color: gridColor,
            drawBorder: false
          },
          ticks: {
            color: textColor,
            font: {
              size: 12
            }
          },
          border: {
            display: false
          }
        }
      }
    };

    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.applyChartGradient();
  }

  navigateToLobby(lobbyId: string): void {
    if (!lobbyId) return;
    this.router.navigate(['/admin/lobbies'], { queryParams: { search: lobbyId } });
  }

  navigateToUser(userId: string): void {
    if (!userId) return;
    this.router.navigate(['/admin/users'], { queryParams: { search: userId } });
  }

  loadDashboard(dateRange: string = '30d') {
    this.isLoading.set(true);
    this.adminService.getAdminDashboard(dateRange).subscribe({
      next: (res) => {
        const data: any = res.data;
        const revenueByDay = data?.revenueByDay?.length ? data.revenueByDay : this.getMockRevenueData();
        const lobbies = data?.recentLobbies?.length ? data.recentLobbies : this.getMockLobbies();
        const users = data?.recentUsers?.length ? data.recentUsers : this.getMockUsers();

        const labels = revenueByDay.map((i: any) => i.date);
        const values = revenueByDay.map((i: any) => i.amount);

        this.chartData = {
          labels,
          datasets: [
            {
              label: 'Revenue',
              data: values,
              fill: true,
              borderColor: '#ec4899',
              backgroundColor: 'rgba(236, 72, 153, 0.04)',
              tension: 0.4,
              pointRadius: new Array(values.length).fill(5),
              pointHoverRadius: 6,
              pointBackgroundColor: '#ffffff',
              pointBorderColor: '#ec4899',
              pointBorderWidth: 2,
              pointHoverBackgroundColor: '#ec4899',
              pointHoverBorderColor: '#ffffff',
              pointHoverBorderWidth: 2
            }
          ]
        };

        setTimeout(() => this.applyChartGradient());


        this.kpis = [
          { label: 'Active Lobbies', value: data?.totalLobbies || 234, image: 'assets/images/dashboard/heart.png', color: '#fce4ec' },
          { label: 'Active Users', value: data?.totalUsers || 2676, image: 'assets/images/dashboard/user.png', color: '#e3f2fd' },
          { label: 'Total Revenue', value: data?.totalRevenue || 12450, image: 'assets/images/dashboard/revenue.png', color: '#f3e5f5', prefix: '$' }
        ];

        this.recentLobbies = lobbies;
        this.recentUsers = users;
        this.isLoading.set(false);
        this.loaderService.hide();
      },
      error: (err) => {
        console.error('Failed to load dashboard', err);
        this.useFallbackMockData();
        this.isLoading.set(false);
        this.loaderService.hide();
      }
    });
  }

  getMockRevenueData() {
    return [
      { date: 'Mon', amount: 2750 },
      { date: 'Tue', amount: 1600 },
      { date: 'Wed', amount: 2900 },
      { date: 'Thu', amount: 1800 },
      { date: 'Fri', amount: 1200 },
      { date: 'Sat', amount: 2678 },
      { date: 'Sun', amount: 700 },
      { date: 'Mon', amount: 1750 },
      { date: 'Tue', amount: 3400 },
      { date: 'Wed', amount: 3700 }
    ];
  }

  getMockLobbies() {
    return [
      { _id: 'LOB1024', hostName: 'Clara', interest: 'Speed Dating', name: 'Speed Dating', type: 'Normal', participants: 12, revenue: 120, status: 'Active', createdAt: '2026-06-19' },
      { _id: 'LOB1025', hostName: 'Sophia', interest: 'Silent Disco', name: 'Silent Disco', type: 'VIP', participants: 5, revenue: 50, status: 'Pending', createdAt: '2026-06-19' },
      { _id: 'LOB1026', hostName: 'James', interest: 'Blind Date', name: 'Blind Date', type: 'Normal', participants: 20, revenue: 200, status: 'Completed', createdAt: '2026-06-19' },
      { _id: 'LOB1027', hostName: 'Mia', interest: 'Wine Tasting', name: 'Wine Tasting', type: 'Premium', participants: 15, revenue: 150, status: 'Active', createdAt: '2026-06-19' },
      { _id: 'LOB1028', hostName: 'Ayush', interest: 'Game Night', name: 'Game Night', type: 'Normal', participants: 8, revenue: 80, status: 'Active', createdAt: '2026-06-19' }
    ] as any;
  }

  getMockUsers() {
    return [
      { _id: 'USR001', name: 'Agness Murisi', email: 'murisinancy@gmail.com', joinedLobbies: 3, totalSpent: 45, joinedAt: '2026-06-19' },
      { _id: 'USR002', name: 'Ayush Bajaj', email: 'ayush.bajaj@autviz.in', joinedLobbies: 5, totalSpent: 75, joinedAt: '2026-06-19' },
      { _id: 'USR003', name: 'Sarah Johnson', email: 'sarah.j@email.com', joinedLobbies: 2, totalSpent: 30, joinedAt: '2026-06-19' },
      { _id: 'USR004', name: 'Mike Chen', email: 'mike.chen@email.com', joinedLobbies: 8, totalSpent: 120, joinedAt: '2026-06-19' },
      { _id: 'USR005', name: 'Alex Rivera', email: 'alex.r@email.com', joinedLobbies: 4, totalSpent: 60, joinedAt: '2026-06-19' }
    ] as any;
  }

  useFallbackMockData() {
    this.kpis = [
      { label: 'Active Lobbies', value: 1235, image: 'assets/images/dashboard/heart.png', color: '#fce4ec' },
      { label: 'Active Users', value: 8995000, image: 'assets/images/dashboard/user.png', color: '#e3f2fd' },
      { label: 'Total Revenue', value: 100000, image: 'assets/images/dashboard/revenue.png', color: '#f3e5f5', prefix: '$' }
    ];

    const revenueByDay = this.getMockRevenueData();
    const labels = revenueByDay.map((i) => i.date);
    const values = revenueByDay.map((i) => i.amount);

    this.chartData = {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: values,
          fill: true,
          borderColor: '#ec4899',
          backgroundColor: 'rgba(236, 72, 153, 0.04)',
          tension: 0.4,
          pointRadius: [0, 0, 5, 5, 0, 0, 0, 5, 5, 0],
          pointHoverRadius: 6,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: '#ec4899',
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#ec4899',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2
        }
      ]
    };

    setTimeout(() => this.applyChartGradient());

    this.recentLobbies = this.getMockLobbies();
    this.recentUsers = this.getMockUsers();
  }

  applyChartGradient() {
    const canvas = this.chart?.getCanvas();
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas || !this.chartData?.datasets?.length) return;

    const gradientStroke = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradientStroke.addColorStop(0, '#ec4899');
    gradientStroke.addColorStop(1, '#db2777');

    const gradientFill = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradientFill.addColorStop(0, 'rgba(236, 72, 153, 0.04)');
    gradientFill.addColorStop(1, 'rgba(236, 72, 153, 0.0)');

    this.chartData.datasets.forEach((d: any) => {
      d.borderColor = gradientStroke;
      d.backgroundColor = gradientFill;
    });

    this.chart?.refresh();
  }

  getStatusSeverity(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getLobbyHostName(lobby: any): string {
    const first = lobby.host?.firstName?.trim();
    const last = lobby.host?.lastName?.trim();
    const name = first || last ? `${first ?? ''} ${last ?? ''}`.trim() : lobby.hostName?.trim();
    return name || 'N/A';
  }

  getLobbyHostInitials(lobby: any): string {
    const name = this.getLobbyHostName(lobby);
    if (name === 'N/A') return 'N/A';
    return name.charAt(0).toUpperCase();
  }

  getUserName(user: any): string {
    const first = user.FirstName?.trim();
    const last = user.LastName?.trim();
    const name = first || last ? `${first ?? ''} ${last ?? ''}`.trim() : user.name?.trim();
    return name || 'N/A';
  }

  getUserInitials(user: any): string {
    const name = this.getUserName(user);
    if (name === 'N/A') return 'N/A';
    return name.charAt(0).toUpperCase();
  }
}